"use client";

import * as React from "react";
import { ArrowLeft, X, Clipboard } from "lucide-react";
import { DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AddPasteTextDialogProps {
  onBack: () => void;
  onClose: () => void;
  onAddFile: (file: File) => void;
}

export function AddPasteTextDialogContent({
  onBack,
  onClose,
  onAddFile,
}: AddPasteTextDialogProps) {
  const [input, setInput] = React.useState("");
  const [hasError, setHasError] = React.useState(false);

  const addText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    try {
      const file = new File([trimmed], `pasted-text-${Date.now()}.txt`, {
        type: "text/plain",
      });

      onAddFile(file);
      setInput("");
      setHasError(false);
    } catch {
      setHasError(true);
    }
  };

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    try {
      const file = new File([trimmed], `pasted-text-${Date.now()}.txt`, {
        type: "text/plain",
      });

      onAddFile(file);
      setInput("");
      setHasError(false);
    } catch {
      setHasError(true);
    }
  };

  return (
    <>
      <DialogHeader className="relative flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8 hover:bg-theme-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">Paste Text</h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 hover:bg-theme-1 absolute right-0 top-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </DialogHeader>

        <div className="space-y-4 -mt-4">
          <p className="text-theme-1">Paste in text to use as a source to study with.</p>

          <div
            className={cn(
              "flex items-start gap-2 rounded-md border px-3 py-3",
              hasError && "border-red-500"
            )}
          >
            <Clipboard className="h-4 w-4 text-theme-1 shrink-0 mt-0.5" />

            <textarea
              placeholder="Paste text"
              value={input}
              rows={8}
              onChange={(e) => {
                setInput(e.target.value);
                setHasError(false);
              }}
              className={cn(
                "flex-1 w-full bg-transparent text-sm outline-none placeholder:text-theme-1 resize-none",
                hasError && "text-red-600 placeholder:text-red-400"
              )}
            />

          </div>

          {/* Add button in bottom right */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleAdd}
              disabled={!input.trim()}
              className="px-6 bg-brand-gradient text-theme-0 text-sm hover:brightness-90 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
            >
              Add to sources
            </Button>
          </div>
        </div>
    </>
  );
}
